import React, { useState, useMemo, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  CssBaseline,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import {
  Logout,
  Person,
  Payment,
  Lock,
  Wallet,
  Engineering,
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  Settings as SettingsIcon,
  PersonAddAlt1 as PersonAddIcon,
  MoveToInbox as MoveToInboxIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  NotificationsActive as NotificationsActiveIcon,
  SupportAgent as SupportAgentIcon,
  VerifiedUser as VerifiedUserIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";

import OtsLogo from "./OtsLogo";
import LogoutDialog from "./LogoutDialog";
import ThemePicker from "./ThemePicker";
import { useTheme as useAppTheme } from "../context/ThemeContext";
import { themeBg } from "../config/themes";
import { toggleSidebar, setSidebarOpen } from "../redux/reducer/SidebarSlice";
import "./ownerTheme.css";
import LiveNotifications from "./LiveNotifications";

/* Widths kept in sync with ownerTheme.css so page content always lines up.
   The rail stays under the 80px that index.css reserves for `.sidebar-closed`
   and the open drawer under its 257px, so every owner page keeps its layout
   without being touched. */
const DRAWER_WIDTH = 240;
const RAIL_WIDTH = 76;
const MOBILE_DRAWER_WIDTH = 284;
const APPBAR_HEIGHT = { xs: 64, md: 76 };

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up("md")]: {
    marginLeft: open ? DRAWER_WIDTH : RAIL_WIDTH,
    width: `calc(100% - ${open ? DRAWER_WIDTH : RAIL_WIDTH}px)`,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginLeft: 0,
  },
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? DRAWER_WIDTH : RAIL_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    width: open ? DRAWER_WIDTH : RAIL_WIDTH,
    overflowX: "hidden",
    border: "none",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

export default function OwnerWrapper() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const open = useSelector((state) => state.sidebar.open);
  const { validateUser, loginUser } = useSelector((state) => state);
  const user = validateUser?.UserValidate?.data?.user;

  const { theme: appTheme, setThemeScope } = useAppTheme();
  const isMobile = useMediaQuery("(max-width:900px)");

  /* Owners keep their own colour choice, separate from the tenant portal */
  useEffect(() => {
    setThemeScope("owner");
  }, [setThemeScope]);

  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorNotif, setAnchorNotif] = useState(null);

  const pathname = location.pathname;
  const profileOpen = Boolean(anchorEl);
  const notifOpen = Boolean(anchorNotif);

  const displayName =
    loginUser?.login?.data?.data?.name ||
    user?.name ||
    localStorage.getItem("name") ||
    "Owner";

  const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

  const unreadCount = useMemo(
    () => (user?.notifications || []).filter((n) => !n.read).length,
    [user]
  );

  /* Expose the active theme to CSS and flag the owner area for scoped styles */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--owner-primary", appTheme.primary);
    root.style.setProperty("--owner-primary-hover", appTheme.primaryHover);
    root.style.setProperty("--owner-secondary", appTheme.secondary);
    root.style.setProperty("--owner-light", appTheme.lightBg);
    root.style.setProperty("--owner-nav", appTheme.navBg);
    document.body.classList.add("owner-mode");
    return () => document.body.classList.remove("owner-mode");
  }, [appTheme]);

  /* Collapse to a rail automatically on small screens */
  useEffect(() => {
    if (isMobile && open) dispatch(setSidebarOpen(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  /* ------------------------------------------------------------------ */
  /* Navigation model — same gates the previous sidebar used             */
  /* ------------------------------------------------------------------ */
  const requests = user?.requests || [];
  const hasRequests = requests.length > 0;
  const pendingRequests = requests.filter((r) => r.status === "pending").length;

  /* Rent tracking, rent collection, escrow and maintenance only make sense
     once someone is actually living in a flat — a verified payment is a
     booking, not a tenancy, and a move-in still waiting on the owner does
     not open these doors. */
  const hasLivingTenant = requests.some(
    (val) =>
      val.paymentStatus === "verified" &&
      val.moveInStatus !== "awaiting" &&
      val.moveInStatus !== "cancelled"
  );

  /* My Tenants itself is different: a booked-and-paid flat with the tenant
     still "awaiting" move-in belongs there too, shown as not-yet-moved-in —
     the owner needs to be able to see their property is rented out the
     moment it's paid for, not only once someone has physically shown up.
     Agreement/bills/rent-collection stay gated behind hasLivingTenant, since
     none of that applies before move-in. */
  const hasRentedProperty = requests.some(
    (val) => val.paymentStatus === "verified" && val.moveInStatus !== "cancelled"
  );

  /* Properties Setting configures existing flats. With nothing listed yet the
     page has nothing to configure, so the row only appears once the owner
     actually has a property. A tenant request can only exist against a listed
     flat, so it counts as evidence too. */
  const hasProperties = (user?.flats || []).length > 0 || hasRequests;

  /* The wallet exists for money OTS is holding on the owner's behalf: a tenant
     pays into the OTS account and the owner withdraws from here. Until such a
     payment has landed there is no balance and nothing to withdraw, so the row
     stays hidden. "verifying" counts — the money has reached OTS, the owner is
     only being asked to confirm it.

     Cash never reaches OTS at all, though — it goes tenant-to-owner directly
     — but a cash payment gets the exact same paymentStatus progression
     (verifying -> verified) once the owner confirms it, since that status
     tracks "has this payment been checked", not "did the money land at
     OTS". Without also checking paymentMethod, the Wallet row (and its
     promise of money to withdraw) showed up for owners who had only ever
     been paid in cash. */
  const hasOtsHeldPayment = requests.some(
    (val) =>
      (val.paymentStatus === "verifying" || val.paymentStatus === "verified") &&
      val.paymentMethod !== "cash"
  );
  const walletBalance = Number(user?.walletBalance) || 0;
  const hasWallet = walletBalance > 0 || hasOtsHeldPayment;

  const sections = [
    {
      label: "Overview",
      items: [
        { label: "Dashboard", icon: <HomeIcon />, path: "/landlord-dashboard" },
      ],
    },
    {
      label: "Portfolio",
      items: [
        {
          label: "My Properties",
          icon: <ApartmentIcon />,
          path: "/my-properties",
        },
        hasProperties && {
          label: "Properties Setting",
          // This is the step that sends the property to the admin —
          // that wasn't at all obvious from the name alone.
          caption: "Apply rules to start the approval process",
          icon: <SettingsIcon />,
          path: "/properties-setting",
        },
      ].filter(Boolean),
    },
    {
      label: "Tenants",
      items: [
        hasRequests && {
          label: "Add New Tenant",
          icon: <PersonAddIcon />,
          path: "/add-new-tenant",
        },
        hasRequests && {
          label: "Tenant New Request",
          icon: <MoveToInboxIcon />,
          path: "/new-request",
          badge: pendingRequests,
          highlight: pendingRequests > 0,
        },
        hasRentedProperty && {
          label: "My Tenants",
          // Bill upload, agreement, and move-out are all reached from within
          // this same row — there's no separate sidebar tab for them, so the
          // caption points the way. Booked-but-not-moved-in
          // properties show here too now, just without those actions.
          caption: "Bills, agreement, move-out",
          icon: <PeopleIcon />,
          path: "/my-tenants",
          /* The old sidebar kept this row lit across every page reached from
             it — keep that, or the owner loses their place mid-journey. */
          match: [
            "/my-tenants",
            "landlord-functionality",
            "agreement",
            "upload-Bill",
            "pending-bill",
            "previous-bill",
          ],
        },
        hasLivingTenant && {
          label: "Maintenance Request",
          icon: <Engineering />,
          path: "/maintenance-request",
        },
      ].filter(Boolean),
    },
    {
      /* Four money-related pages used to have near-identical names ("Rent
         Tracker", "Rent Collection", "Escrow Management", "Wallet") and the
         owner couldn't tell which one to open for a given need. The names
         now match the question each page answers, and a caption underneath
         states what it does. */
      label: "Money",
      items: [
        hasLivingTenant && {
          label: "Rent & Bills",
          caption: "What each tenant still owes",
          icon: <AccountBalanceIcon />,
          path: "/rent-tracker",
        },
        hasLivingTenant && {
          label: "Reminders",
          caption: "Monthly summary + send reminders",
          icon: <Payment />,
          path: "/rent-collection",
        },
        hasLivingTenant && {
          label: "Escrow",
          caption: "Advance & pehla rent — hold/release",
          icon: <Lock />,
          path: "/escrow-management",
        },
        hasWallet && {
          label: "Wallet",
          caption: "Balance aur withdrawal",
          icon: <Wallet />,
          path: "/wallet",
        },
      ].filter(Boolean),
    },
    {
      label: "Account",
      items: [
        { label: "My Profile", icon: <Person />, path: "/profile" },
        {
          label: "Notifications",
          icon: <NotificationsActiveIcon />,
          path: "/notifications",
          badge: unreadCount,
        },
      ],
    },
  ].filter((section) => section.items.length > 0);

  const isActive = (item) => {
    if (Array.isArray(item.match)) {
      return item.match.some((fragment) =>
        fragment.startsWith("/")
          ? pathname === fragment
          : pathname.includes(fragment)
      );
    }
    return item.match ? pathname.includes(item.match) : pathname === item.path;
  };

  const activeItem = sections
    .flatMap((s) => s.items)
    .find((item) => isActive(item));

  const goTo = (path) => {
    navigate(path);
    if (isMobile) dispatch(setSidebarOpen(false));
  };

  /* ------------------------------------------------------------------ */
  /* Sidebar                                                             */
  /* ------------------------------------------------------------------ */
  const railMode = !isMobile && !open;
  const expanded = !railMode;

  const NavButton = ({ item }) => {
    const active = isActive(item);
    return (
      <Tooltip title={railMode ? item.label : ""} placement="right" arrow>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => goTo(item.path)}
          onKeyDown={(e) => e.key === "Enter" && goTo(item.path)}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 1.6,
            mx: railMode ? 1 : 1.5,
            mb: 0.75,
            px: railMode ? 0 : 1.5,
            py: 1.25,
            justifyContent: railMode ? "center" : "flex-start",
            borderRadius: "14px",
            cursor: "pointer",
            color: active ? "#fff" : "rgba(255,255,255,0.66)",
            background: active
              ? `linear-gradient(120deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`
              : "transparent",
            boxShadow: active ? `0 12px 24px -12px ${appTheme.primary}` : "none",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: active
                ? `linear-gradient(120deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`
                : "rgba(255,255,255,0.09)",
              color: "#fff",
              transform: railMode ? "scale(1.06)" : "translateX(4px)",
            },
            "&:focus-visible": {
              outline: `2px solid ${appTheme.secondary}`,
              outlineOffset: 2,
            },
          }}
        >
          <Badge
            color="error"
            badgeContent={item.badge || 0}
            invisible={!item.badge}
            sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 16, minWidth: 16 } }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "& svg": { fontSize: 21 },
              }}
            >
              {item.icon}
            </Box>
          </Badge>

          {expanded && (
            /* A short line under the label: what this page actually does.
               "Rent Tracker", "Rent Collection", "Escrow", "Wallet" — four
               names the owner used to find near-identical now each state
               their own purpose. */
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 14.5,
                  fontWeight: active ? 600 : 500,
                  letterSpacing: 0.2,
                }}
              >
                {item.label}
              </Typography>
              {item.caption && (
                <Typography
                  noWrap
                  sx={{
                    fontSize: 11,
                    opacity: 0.6,
                    letterSpacing: 0.1,
                    lineHeight: 1.3,
                  }}
                >
                  {item.caption}
                </Typography>
              )}
            </Box>
          )}

          {expanded && item.highlight && !active && (
            <Box
              className="owner-pulse"
              sx={{
                ml: "auto",
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: appTheme.secondary,
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  };

  const drawerContent = (
    <Box
      className="owner-drawer-glow"
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: railMode ? "center" : "space-between",
          gap: 1,
          px: railMode ? 1 : 2,
          height: APPBAR_HEIGHT,
          flexShrink: 0,
        }}
      >
        <Box
          onClick={() => goTo("/landlord-dashboard")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            cursor: "pointer",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 42,
              width: 42,
              flexShrink: 0,
              borderRadius: "13px",
              bgcolor: "rgba(255,255,255,0.12)",
              border: `1px solid ${appTheme.secondary}33`,
              transition: "all 0.3s ease",
            }}
          >
            <OtsLogo height={30} />
          </Box>
          {expanded && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 16,
                  lineHeight: 1.2,
                }}
              >
                OTS
              </Typography>
              <Typography
                noWrap
                sx={{
                  color: appTheme.secondary,
                  fontSize: 10.5,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                }}
              >
                Owner Portal
              </Typography>
            </Box>
          )}
        </Box>

        {isMobile && (
          <IconButton
            onClick={() => dispatch(setSidebarOpen(false))}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Profile card */}
      <Box sx={{ px: railMode ? 1 : 2, pb: 2, position: "relative" }}>
        <Box
          onClick={() => goTo("/profile")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            p: railMode ? 0.75 : 1.4,
            justifyContent: railMode ? "center" : "flex-start",
            borderRadius: "16px",
            cursor: "pointer",
            bgcolor: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.09)",
            transition: "all 0.25s ease",
            "&:hover": { bgcolor: "rgba(255,255,255,0.13)" },
          }}
        >
          <Avatar
            src={user?.profileImage}
            sx={{
              width: railMode ? 36 : 42,
              height: railMode ? 36 : 42,
              border: `2px solid ${appTheme.secondary}`,
              bgcolor: appTheme.primary,
              fontWeight: 700,
            }}
          >
            {capitalize(displayName).charAt(0)}
          </Avatar>
          {expanded && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}
              >
                {capitalize(displayName)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <VerifiedUserIcon
                  sx={{ fontSize: 12, color: appTheme.secondary }}
                />
                <Typography
                  noWrap
                  sx={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}
                >
                  {hasLivingTenant
                    ? "Active Owner"
                    : hasRequests
                    ? "Reviewing requests"
                    : "Owner"}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Navigation */}
      <Box
        className="owner-drawer-paper"
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pb: 1 }}
      >
        {sections.map((section) => (
          <Box key={section.label} sx={{ mb: 1.5 }}>
            {expanded && (
              <Typography
                sx={{
                  px: 3,
                  pb: 0.75,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.35)",
                }}
              >
                {section.label}
              </Typography>
            )}
            {railMode && (
              <Divider
                sx={{ mx: 2, mb: 1, borderColor: "rgba(255,255,255,0.09)" }}
              />
            )}
            {section.items.map((item) => (
              <NavButton key={item.label} item={item} />
            ))}
          </Box>
        ))}
      </Box>

      {/* Footer: support card + logout */}
      <Box sx={{ px: railMode ? 1 : 2, pb: 2, pt: 1, position: "relative" }}>
        {expanded && (
          <Box
            sx={{
              p: 1.8,
              mb: 1.5,
              borderRadius: "16px",
              background: themeBg(
                appTheme,
                `linear-gradient(135deg, ${appTheme.primary}33 0%, ${appTheme.secondary}22 100%)`,
                `${appTheme.primary}26`
              ),
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <SupportAgentIcon sx={{ color: appTheme.secondary, fontSize: 22 }} />
            <Typography
              sx={{ color: "#fff", fontWeight: 600, fontSize: 13, mt: 0.5 }}
            >
              Need a hand?
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.6)", fontSize: 11, mb: 1 }}
            >
              We are here 24/7 for you.
            </Typography>
            <Box
              onClick={() => goTo("/about-us")}
              sx={{
                display: "inline-block",
                px: 1.5,
                py: 0.5,
                borderRadius: "999px",
                fontSize: 11,
                fontWeight: 600,
                color: "#fff",
                cursor: "pointer",
                background: `linear-gradient(120deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`,
              }}
            >
              Get support
            </Box>
          </Box>
        )}

        <Tooltip title={railMode ? "Logout" : ""} placement="right" arrow>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setOpenDialog(true)}
            onKeyDown={(e) => e.key === "Enter" && setOpenDialog(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: railMode ? "center" : "flex-start",
              gap: 1.6,
              px: railMode ? 0 : 1.5,
              py: 1.15,
              borderRadius: "14px",
              cursor: "pointer",
              color: "rgba(255,255,255,0.66)",
              border: "1px solid rgba(255,255,255,0.08)",
              transition: "all 0.25s ease",
              "&:hover": {
                color: "#fff",
                bgcolor: "rgba(244,67,54,0.22)",
                borderColor: "rgba(244,67,54,0.45)",
              },
            }}
          >
            <Logout sx={{ fontSize: 20 }} />
            {expanded && (
              <Typography sx={{ fontSize: 14.5, fontWeight: 500 }}>
                Logout
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  const drawerPaperSx = {
    background: themeBg(
      appTheme,
      `linear-gradient(175deg, ${appTheme.navBg} 0%, ${appTheme.navBg} 55%, ${appTheme.primary}22 100%)`,
      appTheme.navBg
    ),
    color: "#fff",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "0 0 40px rgba(0,0,0,0.18)",
  };

  /* ------------------------------------------------------------------ */
  /* Mobile bottom bar                                                   */
  /* ------------------------------------------------------------------ */
  const bottomItems = [
    { label: "Home", icon: <HomeIcon />, path: "/landlord-dashboard" },
    { label: "Properties", icon: <ApartmentIcon />, path: "/my-properties" },
    hasRentedProperty
      ? { label: "Tenants", icon: <PeopleIcon />, path: "/my-tenants" }
      : hasRequests
      ? { label: "Requests", icon: <MoveToInboxIcon />, path: "/new-request" }
      : hasWallet
      ? { label: "Wallet", icon: <Wallet />, path: "/wallet" }
      : {
          label: "Alerts",
          icon: <NotificationsActiveIcon />,
          path: "/notifications",
        },
    { label: "Profile", icon: <Person />, path: "/profile" },
  ];

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        <AppBar
          position="fixed"
          open={open}
          elevation={0}
          sx={{
            bgcolor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(16px)",
            color: "#16203a",
            borderBottom: "1px solid rgba(18,32,58,0.07)",
          }}
        >
          <Toolbar
            sx={{
              minHeight: `${APPBAR_HEIGHT.xs}px !important`,
              [`@media (min-width:900px)`]: {
                minHeight: `${APPBAR_HEIGHT.md}px !important`,
              },
              gap: 1,
              px: { xs: 1.5, md: 3 },
            }}
          >
            {!isMobile && (
              <IconButton
                onClick={() => dispatch(toggleSidebar())}
                aria-label="toggle navigation"
                sx={{
                  color: appTheme.primary,
                  bgcolor: `${appTheme.primary}12`,
                  borderRadius: "12px",
                  "&:hover": { bgcolor: `${appTheme.primary}24` },
                }}
              >
                {open ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            )}

            {/* Mobile: just the OTS mark where the hamburger + title used to be — the bottom bar is the real nav now */}
            {isMobile && (
              <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                <OtsLogo height={30} />
              </Box>
            )}

            {/* Page heading (desktop only) */}
            {!isMobile && (
              <Box sx={{ minWidth: 0, flexGrow: 1, ml: { xs: 0.5, md: 1 } }}>
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 15, md: 18 },
                    lineHeight: 1.25,
                  }}
                >
                  {activeItem?.label || "Owner Portal"}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    fontSize: { xs: 11, md: 12 },
                    color: "text.secondary",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  Welcome back, {capitalize(displayName)} 👋
                </Typography>
              </Box>
            )}

            {/* Theme switcher — the owner's own choice, saved on this device */}
            <ThemePicker variant="inline" light={false} size={40} />

            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton
                onClick={(e) => setAnchorNotif(e.currentTarget)}
                sx={{
                  color: appTheme.primary,
                  bgcolor: `${appTheme.primary}12`,
                  width: 40,
                  height: 40,
                  "&:hover": { bgcolor: `${appTheme.primary}24` },
                }}
              >
                <Badge
                  color="error"
                  badgeContent={unreadCount}
                  invisible={unreadCount === 0}
                >
                  <NotificationsIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Avatar */}
            <Tooltip title="Account">
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ p: 0.4, ml: 0.5 }}
              >
                <Avatar
                  src={user?.profileImage}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: appTheme.primary,
                    fontSize: 15,
                    fontWeight: 700,
                    border: `2px solid ${appTheme.primary}44`,
                  }}
                >
                  {capitalize(displayName).charAt(0)}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        {isMobile ? (
          <MuiDrawer
            variant="temporary"
            open={open}
            onClose={() => dispatch(setSidebarOpen(false))}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: { width: MOBILE_DRAWER_WIDTH, ...drawerPaperSx },
            }}
          >
            {drawerContent}
          </MuiDrawer>
        ) : (
          <Drawer
            variant="permanent"
            open={open}
            PaperProps={{ sx: drawerPaperSx }}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* Spacer that pushes page content below the fixed app bar */}
        <Box component="main" sx={{ flexGrow: 1, height: { xs: 76, md: 92 } }} />
      </Box>

      {/* Notifications dropdown */}
      <Menu
        anchorEl={anchorNotif}
        open={notifOpen}
        onClose={() => setAnchorNotif(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            elevation: 6,
            sx: {
              width: { xs: 290, sm: 340 },
              maxHeight: 420,
              mt: 1.5,
              borderRadius: 3,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ddd",
                borderRadius: 3,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(120deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`,
            color: "#fff",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
            Notifications
          </Typography>
          <Typography sx={{ fontSize: 11.5, opacity: 0.9 }}>
            {unreadCount > 0 ? `${unreadCount} unread` : "You are all caught up"}
          </Typography>
        </Box>

        {user?.notifications?.length > 0 ? (
          user.notifications
            .slice()
            .reverse()
            .map((notif, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  setAnchorNotif(null);
                  navigate("/notifications");
                }}
                sx={{
                  whiteSpace: "normal",
                  alignItems: "flex-start",
                  gap: 1.2,
                  py: 1.2,
                  borderBottom: "1px solid #f1f1f4",
                  bgcolor: notif.read ? "inherit" : `${appTheme.primary}0d`,
                  "&:hover": { bgcolor: appTheme.lightBg },
                }}
              >
                <Box
                  sx={{
                    mt: 0.4,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: notif.read ? "#d6d6de" : appTheme.primary,
                  }}
                />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: notif.read ? 400 : 600 }}
                  >
                    {notif.message || "New notification"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.3 }}
                  >
                    {notif.date ? new Date(notif.date).toLocaleString() : ""}
                  </Typography>
                </Box>
              </MenuItem>
            ))
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <NotificationsIcon sx={{ fontSize: 34, color: "#d8d8e0" }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No notifications yet.
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Account dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={profileOpen}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            elevation: 6,
            sx: { mt: 1.5, borderRadius: 3, minWidth: 218, overflow: "hidden" },
          },
        }}
      >
        <Box
          sx={{ px: 2, py: 1.5, display: "flex", gap: 1.4, alignItems: "center" }}
        >
          <Avatar
            src={user?.profileImage}
            sx={{ width: 40, height: 40, bgcolor: appTheme.primary }}
          >
            {capitalize(displayName).charAt(0)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>
              {capitalize(displayName)}
            </Typography>
            <Chip
              size="small"
              label="Owner"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 600,
                color: appTheme.primary,
                bgcolor: appTheme.lightBg,
              }}
            />
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={() => navigate("/profile")}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        {hasWallet && (
          <MenuItem onClick={() => navigate("/wallet")}>
            <ListItemIcon>
              <Wallet fontSize="small" />
            </ListItemIcon>
            Wallet
          </MenuItem>
        )}
        <MenuItem onClick={() => navigate("/notifications")}>
          <ListItemIcon>
            <NotificationsActiveIcon fontSize="small" />
          </ListItemIcon>
          Notifications
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setOpenDialog(true)} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <Logout fontSize="small" color="error" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1150,
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            px: 1,
            py: 0.75,
            bgcolor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(16px)",
            borderTop: "1px solid rgba(18,32,58,0.08)",
            boxShadow: "0 -6px 24px -12px rgba(18,32,58,0.35)",
          }}
        >
          {bottomItems.map((item) => {
            const active = pathname === item.path;
            return (
              <Box
                key={item.label}
                onClick={() => navigate(item.path)}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.2,
                  py: 0.6,
                  borderRadius: "14px",
                  cursor: "pointer",
                  color: active ? "#fff" : "text.secondary",
                  background: active
                    ? `linear-gradient(120deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`
                    : "transparent",
                  transition: "all 0.25s ease",
                  "& svg": { fontSize: 20 },
                }}
              >
                {item.icon}
                <Typography sx={{ fontSize: 10, fontWeight: active ? 700 : 500 }}>
                  {item.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <LogoutDialog open={openDialog} onClose={() => setOpenDialog(false)} />
      <LiveNotifications />
    </>
  );
}
